import { useParams } from '@solidjs/router';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { AuditTimeline } from '../components/AuditTimeline';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { Panel } from '../components/Panel';
import { isAnsweredRefusal } from '../components/RunStreamRefusal';
import { SessionPageHeader } from '../components/SessionPageHeader';
import { SessionReplayPanel } from '../components/SessionReplayPanel';
import { SessionStreamRefusal } from '../components/SessionStreamRefusal';
import { Skeleton } from '../components/Skeleton';
import { t } from '../i18n';
import type { StreamRefusal } from '../lib/appendLogStream';
import { downloadSessionJsonl } from '../lib/auditExport';
import { createMutation } from '../lib/mutation';
import { SESSION_TOKEN } from '../lib/session';
import { useSessionDetail } from '../lib/sessionDetail';
import { useSessionStream } from '../lib/sessionStream';

// Full session timeline view — a live SSE feed of the session's AuditEvents
// (#212) under the `GET /v1/sessions/:id` header projection (#980): repo,
// trigger, outcome, model, entity link. A failed detail read degrades to the
// generic header — the stream is the page, the projection is context.

// `token` is injectable for tests (mirrors Fleet / FleetAudit); the router
// mounts this without props → SESSION_TOKEN (dev session or null).
export function AuditSession(
  props: { readonly token?: string | null | undefined } = {},
): JSX.Element {
  const token = props.token !== undefined ? props.token : SESSION_TOKEN;
  const params = useParams<{ session_id: string }>();
  const { events, status, refusal, retry } = useSessionStream(() => params.session_id, token);
  const detail = useSessionDetail(() => params.session_id, token);
  const [replayOpen, setReplayOpen] = createSignal(false);

  // Permalink landing (#812): once the linked event's row is in the stream,
  // scroll it into view — the `:target` style highlights it. Runs until the
  // anchor appears (the backfill streams progressively), then arms off.
  let landed = window.location.hash.length <= 1;
  createEffect(() => {
    if (landed || events().length === 0) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) {
      el.scrollIntoView({ block: 'center' });
      landed = true;
    }
  });

  // The alert below renders a FIXED sentence rather than `onExport.error()`,
  // which is the one place this app does not print the failure it caught:
  // `downloadSessionJsonl` throws an `ApiError` whose message is the request and
  // its status (`GET <path> → <status>`, or a trailer that made no progress) — a
  // wire string, not a translated one, and `errorReason` would put it in front
  // of a reader. A drain the route cut short is followed to its end or fails the
  // whole export, so this alert is also what a partial trail reads as (plan 101
  // slice 17 row 17-19). `fallback` names the same key so the two cannot drift.
  const onExport = createMutation({
    call: () => downloadSessionJsonl(params.session_id, token),
    fallback: t('audit_session.export_error'),
  });

  return (
    <>
      <SessionPageHeader
        detail={detail.data}
        status={status()}
        exporting={onExport.pending()}
        replayOpen={replayOpen()}
        onExport={() => void onExport.run()}
        onToggleReplay={() => setReplayOpen((v) => !v)}
      />
      <Show when={onExport.error() !== null}>
        <div class="alert alert--destructive" role="alert">
          <p class="alert__title">{t('audit_session.export_error')}</p>
        </div>
      </Show>

      <Show when={replayOpen()}>
        <SessionReplayPanel sessionId={params.session_id} token={token} />
      </Show>

      <Panel
        title={t('audit.events')}
        action={
          <span class="audit-id">
            {t('audit.session_label')} <code>{params.session_id}</code>
          </span>
        }
      >
        {/* An ANSWERED refusal (a 404, or no credential at all) is reported as
            itself — outermost, because both reach this panel wearing a `status`
            that reads as something else: the 404 as a retryable error whose
            Retry can only 404 again, the missing credential as `done` with zero
            events, which the empty branch below then calls "No events yet." */}
        <Show
          when={refusal() !== null && isAnsweredRefusal(refusal() as StreamRefusal)}
          fallback={
            <Show
              when={status() !== 'error'}
              fallback={<ErrorState message={t('audit_session.status_error')} onRetry={retry} />}
            >
              <Show
                when={events().length > 0}
                fallback={
                  <Show
                    when={status() === 'connecting'}
                    fallback={<EmptyState message={t('audit.empty')} />}
                  >
                    <div
                      class="audit-timeline__loading"
                      role="status"
                      aria-label={t('states.loading')}
                    >
                      <Skeleton height="4.5rem" />
                      <Skeleton height="4.5rem" />
                      <Skeleton height="4.5rem" />
                    </div>
                  </Show>
                }
              >
                <AuditTimeline events={events()} />
              </Show>
            </Show>
          }
        >
          <SessionStreamRefusal refusal={refusal() as StreamRefusal} token={token} />
        </Show>
      </Panel>
    </>
  );
}
